"use client";
import { Button } from "./button";
import XClose from "@repo/icons/react/XClose";
import type { HTMLAttributes } from "react";

import { cn } from "@/lib/utils";
import type { ButtonHTMLAttributes, KeyboardEvent, ReactNode } from "react";

export type SearchableComboBoxProps<T> = {
  type?: "multi" | "single";

  identifier: string;

  items: T[];
  selectedItems: T[];
  onItemRemoved: (item: T) => void | Promise<void>;
  /** Text matched against the search query. */
  nameGetter?: (item: T) => string;
  /** Text shown in the input when reopening a single selection. Defaults to `nameGetter`. */
  displayNameGetter?: (item: T) => string;
  /** Stable identity for selections; avoids resetting the input when callers rebuild items. */
  itemKey?: (item: T) => string | number;
  onInputTextChange?: (text: string) => void;
  /** Focus the control on mount (and once it becomes enabled) without opening the menu. */
  autoFocus?: boolean;
  /** Let questionnaire Enter-to-continue fire when no option is highlighted. */
  enterAdvances?: boolean;

  placeholder?: string;
  label?: string;
  disabled?: boolean;
  /** Applied to the CommandInput wrapper (defaults to bordered). */
  inputContainerClassName?: string;
  /** Applied to the closed single-select trigger button. */
  triggerClassName?: string;

  renderSearchBoxLeading?: ({
    inputText,
  }: {
    inputText: string;
  }) => ReactNode | undefined;
  renderCommandEmpty: ({ inputText }: { inputText: string }) => ReactNode;
  /** Called when the menu closes from a click outside, including unmatched text. */
  onDismiss?: (details: { inputText: string; matchCount: number }) => void;
  onInputKeyDown?: (
    event: KeyboardEvent<HTMLInputElement>,
    details: { inputText: string; matchCount: number },
  ) => void;
  renderCommandItem: ({ item }: { item: T }) => ReactNode;
  renderItem?: ({ item }: { item: T }) => ReactNode;
};

export function SearchableComboBoxCreateButton({
  className,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      {...props}
      className={cn(
        "w-full relative flex cursor-default gap-2 select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none disabled:pointer-events-none hover:bg-accent hover:text-accent-foreground data-[disabled=true]:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
        className,
      )}
    />
  );
}

SearchableComboBoxCreateButton.displayName = "SearchableComboBoxCreateButton";

export function SearchableSelections<T>({
  type,
  selectedItems,
  onItemRemoved,
  renderItem,
}: Pick<
  SearchableComboBoxProps<T>,
  "type" | "selectedItems" | "onItemRemoved" | "renderItem"
>) {
  return (
    <>
      {" "}
      {type === "multi" && selectedItems.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-2">
          {selectedItems.map((item, idx) => (
            <Button key={idx} variant="outline" asChild>
              <div>
                {renderItem?.({ item })}

                <div
                  className="p-1 cursor-pointer"
                  onClick={() => onItemRemoved(item)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      onItemRemoved(item);
                    }
                  }}
                >
                  <XClose />
                </div>
              </div>
            </Button>
          ))}
        </div>
      )}
    </>
  );
}

/** cmdk 1.x asChild expects a component child when building its list sizer. */
export function ComboBoxListContent(props: HTMLAttributes<HTMLDivElement>) {
  return <div {...props} />;
}
